<main class="operator-detail-page">
  @if (isLoading) {
    <section class="status-panel" aria-live="polite">
      <mat-spinner diameter="32" aria-hidden="true"></mat-spinner>
      <p>オペレーター個人支援ボードを読み込み中です。</p>
    </section>
  } @else if (
    dashboard == null || summary == null || keyActionTrendChart == null
  ) {
    <section class="status-panel error-panel" role="alert">
      <mat-icon aria-hidden="true">error_outline</mat-icon>
      <p>{{ loadErrorMessage }}</p>
      <button type="button" class="btn btn-secondary" (click)="retry()">
        再試行
      </button>
    </section>
  } @else {
    <nav class="breadcrumb" aria-label="パンくずリスト">
      @if (canManage) {
        <button type="button" (click)="openOperatorList()">
          オペレーター一覧
        </button>
        <span aria-hidden="true">/</span>
      }
      <span>{{ summary.userName }}</span>
    </nav>

    <header class="page-heading">
      <div>
        <p class="eyebrow">Operator Coaching</p>
        <h1>個人支援ボード</h1>
        <p class="page-description">
          実績・評価・対話をつなぎ、次のコールで実行するアクションを具体化します。
        </p>
      </div>
    </header>

    <div class="detail-layout">
      <nav class="section-nav" aria-label="個人支援ボードのセクション">
        <button
          type="button"
          class="section-nav-item"
          [class.active]="activeTab === 'summary'"
          [attr.aria-current]="activeTab === 'summary' ? 'page' : null"
          (click)="selectTab('summary')"
        >
          サマリー
        </button>
        <button
          type="button"
          class="section-nav-item"
          [class.active]="activeTab === 'feedback'"
          [attr.aria-current]="activeTab === 'feedback' ? 'page' : null"
          (click)="selectTab('feedback')"
        >
          フィードバックシート
        </button>
      </nav>
      <div
        class="detail-content"
        #detailContent
        tabindex="-1"
        role="region"
        [attr.aria-label]="
          activeTab === 'summary' ? 'サマリー' : 'フィードバックシート'
        "
      >
        @if (activeTab === 'summary') {
          <div class="summary-grid">
            <section class="card profile-card" aria-label="オペレーター情報">
              <div class="avatar" aria-hidden="true">{{ avatarText }}</div>
              <div>
                <p class="eyebrow profile-eyebrow">OPERATOR</p>
                <h2>{{ summary.userName }}</h2>
                @if (summary.roleTitle != null) {
                  <div class="profile-meta">{{ summary.roleTitle }}</div>
                }
              </div>
            </section>

            @if (summary.latestPeriod == null) {
              <section class="card kpi-empty-state">
                <mat-icon aria-hidden="true">query_stats</mat-icon>
                <p>直近のKPI実績はまだありません。</p>
              </section>
            } @else {
              @for (metric of metricCards; track metric.itemId) {
                <section class="card rate-card">
                  <div class="rate-card-head">
                    <span class="rate-title">{{ metric.label }}</span>
                    <span class="rate-month">{{ latestPeriodText }}</span>
                  </div>
                  <div class="rate-row">
                    <div class="rate-body">
                      <div class="rate-main">
                        {{ metric.actualRateText }}
                        @if (metric.actualRate != null) {
                          <span>%</span>
                        }
                      </div>
                      <div class="rate-formula">{{ metric.formulaText }}</div>
                    </div>
                    <div class="rate-goal">
                      @if (metric.targetRate == null) {
                        <span class="rate-goal-unset">{{
                          metric.targetText
                        }}</span>
                      } @else {
                        <strong>{{ metric.targetText }}</strong>
                        @if (metric.gap != null) {
                          <span class="rate-gap" [class]="metric.gapClass">
                            {{ metric.gapText }} {{ metric.achievementText }}
                          </span>
                        }
                      }
                    </div>
                  </div>
                </section>
              }
            }
          </div>

          <section class="card trend-card" aria-labelledby="trend-heading">
            <div class="section-header">
              <div>
                <h2 id="trend-heading">月次推移</h2>
                <p>
                  実績率は入稿した分子・分母から算出。目標率との差を月ごとに確認できます。
                </p>
              </div>
              @if (trendRangeText != null) {
                <span class="role-chip">{{ trendRangeText }}</span>
              }
            </div>
            <div class="trend-body">
              @for (chart of trendCharts; track chart.itemId) {
                <article class="chart-panel">
                  <h3>{{ chart.label }}</h3>
                  <highcharts-chart
                    [Highcharts]="Highcharts"
                    [options]="chart.options"
                    [oneToOne]="true"
                  />
                </article>
              }
              @if (summary.monthlyTrend.length === 0) {
                <article class="chart-panel chart-panel-full">
                  <div class="section-empty-state">
                    KPI 実績が未登録のため KPI の月次推移は表示できません。
                  </div>
                </article>
              }
              <article
                class="chart-panel chart-panel-full key-action-chart-panel"
              >
                <h3>重点強化 KeyAction スコア</h3>
                @if (
                  summary.selectedKeyActionIds.length > 0 ||
                  keyActionTrendReloadErrorMessage !== '' ||
                  keyActionTrendChart.options != null
                ) {
                  <p class="chart-caption" id="key-action-trend-caption">
                    {{ keyActionTrendChart.rangeText }}
                  </p>
                }
                <div aria-live="polite">
                  @if (keyActionTrendReloadErrorMessage) {
                    <div class="section-empty-state">
                      {{ keyActionTrendReloadErrorMessage }}
                    </div>
                  } @else if (summary.selectedKeyActionIds.length === 0) {
                    <div class="section-empty-state">
                      重点強化 KeyAction を設定すると推移を表示します。
                    </div>
                  } @else if (keyActionTrendChart.options == null) {
                    <div class="section-empty-state">
                      {{ keyActionTrendChart.emptyText }}
                    </div>
                  }
                </div>
                @if (keyActionTrendChart.options; as options) {
                  <highcharts-chart
                    [Highcharts]="Highcharts"
                    [options]="options"
                    [oneToOne]="true"
                    aria-describedby="key-action-trend-caption"
                  />
                }
                @if (summary.selectedKeyActionIds.length > 0) {
                  <div
                    class="key-action-daily-trend"
                    [attr.aria-busy]="isDailyTrendLoading"
                  >
                    <div class="daily-trend-header">
                      <h4 id="key-action-daily-trend-heading">日次推移</h4>
                      <div
                        class="daily-trend-nav"
                        role="group"
                        aria-label="日次推移の対象月"
                      >
                        <button
                          type="button"
                          mat-icon-button
                          class="daily-trend-prev"
                          aria-label="前の月"
                          [disabled]="!canGoDailyTrendPrevious"
                          (click)="onDailyTrendPrevious()"
                        >
                          <mat-icon aria-hidden="true">chevron_left</mat-icon>
                        </button>
                        <span class="daily-trend-period" aria-live="polite">{{
                          dailyTrendPeriodText
                        }}</span>
                        <button
                          type="button"
                          mat-icon-button
                          class="daily-trend-next"
                          aria-label="次の月"
                          [disabled]="!canGoDailyTrendNext"
                          (click)="onDailyTrendNext()"
                        >
                          <mat-icon aria-hidden="true">chevron_right</mat-icon>
                        </button>
                      </div>
                    </div>
                    @if (keyActionDailyTrend; as dailyTrend) {
                      <p
                        class="chart-caption"
                        id="key-action-daily-trend-caption"
                      >
                        {{ dailyTrend.rangeText }}
                      </p>
                    }
                    @if (keyActionTrendReloadErrorMessage) {
                      <div class="section-empty-state">
                        {{ keyActionTrendReloadErrorMessage }}
                      </div>
                    } @else if (dailyTrendErrorMessage) {
                      <div role="alert">
                        <p>{{ dailyTrendErrorMessage }}</p>
                        <button
                          type="button"
                          class="btn btn-ghost btn-sm"
                          [disabled]="isSaving || isDailyTrendLoading"
                          (click)="retryDailyTrend()"
                        >
                          再試行
                        </button>
                      </div>
                    } @else if (keyActionDailyTrend; as dailyTrend) {
                      @if (dailyTrend.options; as options) {
                        <highcharts-chart
                          [Highcharts]="Highcharts"
                          [options]="options"
                          [oneToOne]="true"
                          aria-describedby="key-action-daily-trend-caption"
                        />
                      } @else if (!isDailyTrendLoading) {
                        <div class="section-empty-state">
                          {{ dailyTrend.emptyText }}
                        </div>
                      }
                    }
                    <p class="chart-caption" role="status">
                      {{
                        isDailyTrendLoading ? '日次推移を読み込み中です。' : ''
                      }}
                    </p>
                  </div>
                }
              </article>
            </div>
          </section>

          <section class="card ka-section" aria-labelledby="ka-heading">
            <div class="section-header">
              <div>
                <h2 id="ka-heading">重点強化 KeyAction</h2>
                <p>
                  重点的に実践する項目を最大3つまで設定します。
                  併記の平均スコアは期間を限定せず直近
                  {{ operatorScoreCount }}
                  回のアセスメントから算出します。タグ選択時は、選択したタグに該当するアセスメントのうち直近
                  {{ operatorScoreCount }}
                  回が対象です。月次・日次推移はタグで絞り込まれず、平均スコアとは集計方法が異なります。
                </p>
              </div>
              @if (canManage) {
                <button
                  class="btn btn-ghost btn-sm"
                  type="button"
                  (click)="openSettings()"
                >
                  設定画面を開く
                </button>
              }
            </div>
            @if (tagItems.length > 0) {
              <div class="ka-tag-filter">
                <app-multiple-select
                  fieldName="タグ："
                  [items]="tagItems"
                  [matchOptions]="tagMatchOptions"
                  [matchMode]="tagMatch"
                  (changeSelected)="changeSelectedTags($event)"
                  (changeMatchMode)="changeTagMatch($event)"
                />
                @if (isKeyActionScoresLoading) {
                  <span role="status">平均スコアを更新中...</span>
                }
              </div>
            }
            @if (keyActionScoresErrorMessage) {
              <p class="save-error" role="alert">
                {{ keyActionScoresErrorMessage }}
              </p>
            }
            <app-key-action-selector
              [options]="keyActions"
              [description]="'強化対象を最大3つまで選択できます。'"
              [ariaLabel]="summary.userName + 'の重点強化KeyAction一覧'"
              [readonly]="!canManage || isSaving"
              (selectedIdsChange)="onKeyActionSelectionChanged($event)"
            />
            @if (saveErrorMessage) {
              <p class="save-error" role="alert">{{ saveErrorMessage }}</p>
            }
            <div class="ka-save-row">
              <p class="permission-note">
                ※
                主任・運用責任者のみ変更できます。オペレーターは閲覧のみです。変更内容は「保存」ボタンで確定します。
              </p>
              @if (canManage) {
                <button
                  class="btn btn-primary"
                  type="button"
                  [disabled]="!hasKeyActionChanges || isSaving"
                  (click)="saveKeyActions()"
                >
                  {{ isSaving ? '保存中...' : '保存' }}
                </button>
              }
            </div>
          </section>
        }

        @if (feedbackSheetViewer != null) {
          <app-kpi-feedback-sheet-section
            [hidden]="activeTab !== 'feedback'"
            [dashboardId]="dashboardId"
            [operatorUserId]="operatorUserId"
            [viewer]="feedbackSheetViewer"
            [keyActions]="dashboard.keyActions"
          />
        }
      </div>
    </div>
  }
</main>
